import { FieldDefinition, ValidationError, ValidationResult, RowValidationFunction, GlobalValidationFunction } from './types';

export class ExcelValidator {
  private fields: FieldDefinition[];
  private columnMap: Map<string, number> = new Map();
  private headerMap: Map<string, string> = new Map();

  constructor(fields: FieldDefinition[]) {
    this.fields = fields;
  }

  buildColumnMapFromHeaders(headers: string[]): void {
    this.columnMap.clear();
    this.headerMap.clear();
    headers.forEach((header, index) => {
      const trimmed = header.trim();
      const field = this.fields.find(f =>
        f.label.trim().toLowerCase() === trimmed.toLowerCase() ||
        f.key.trim().toLowerCase() === trimmed.toLowerCase()
      );
      if (field) {
        this.columnMap.set(field.key, index);
        this.headerMap.set(field.key, trimmed);
        this.headerMap.set(field.label, trimmed);
      }
    });
  }

  private validateCell(value: any, field: FieldDefinition, row: Record<string, any>, allRows: Record<string, any>[], rowIndex: number): string | null {
    const processedValue = value === null || value === undefined ? '' : value;
    const stringValue = String(processedValue).trim();

    for (const validation of field.validations) {
      switch (validation.rule) {
        case 'required':
          if (processedValue === '' || stringValue === '') return validation.errorMessage;
          break;
        case 'regex': {
          const regex = typeof validation.value === 'string'
            ? new RegExp(validation.value)
            : validation.value instanceof RegExp ? validation.value : null;
          if (regex && stringValue && !regex.test(stringValue)) return validation.errorMessage;
          break;
        }
        case 'custom':
          if (typeof validation.value === 'function') {
            const error = validation.value(value, row, allRows);
            if (error) return error;
          }
          break;
      }
    }
    return null;
  }

  validateData(
    data: Record<string, any>[],
    rowValidations: RowValidationFunction[] = [],
    globalValidations: GlobalValidationFunction[] = []
  ): ValidationResult {
    const errors: ValidationError[] = [];

    data.forEach((row, rowIndex) => {
      this.fields.forEach((field) => {
        const actualHeader = this.headerMap.get(field.key) || this.headerMap.get(field.label) || field.key;
        let value: any = row[actualHeader] ?? row[field.key] ?? row[field.label] ?? '';
        if (value === undefined || value === null) value = '';

        const errorMessage = this.validateCell(value, field, row, data, rowIndex);
        if (errorMessage) {
          errors.push({ cellRef: '', columnKey: field.key, rowIndex, errorMessage, cellValue: value });
        }
      });

      rowValidations.forEach(validateRow => {
        const rowErrors = validateRow(row, rowIndex, data);
        for (let ei = 0; ei < rowErrors.length; ei++) {
          errors.push(rowErrors[ei]);
        }
      });
    });

    globalValidations.forEach(validateGlobal => {
      const globalErrors = validateGlobal(data);
      for (let ei = 0; ei < globalErrors.length; ei++) {
        errors.push(globalErrors[ei]);
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
      data,
      errorCount: errors.length
    };
  }

  validateHeaders(headers: string[]): { isValid: boolean; missingHeaders: string[]; errorMessage?: string } {
    const foundHeaders = new Set(headers.map(h => h.trim().toLowerCase()));
    const missingHeaders: string[] = [];

    for (const field of this.fields) {
      const labelLower = field.label.trim().toLowerCase();
      const keyLower = field.key.trim().toLowerCase();
      if (!foundHeaders.has(labelLower) && !foundHeaders.has(keyLower)) {
        missingHeaders.push(field.label.trim());
      }
    }

    if (missingHeaders.length > 0) {
      return {
        isValid: false,
        missingHeaders,
        errorMessage: `Missing required headers: ${missingHeaders.join(', ')}. Please use the correct format.`
      };
    }
    return { isValid: true, missingHeaders: [] };
  }
}
